import React, { Component } from 'react';
import { Pagination } from 'react-bootstrap';
import _ from 'lodash';
import { underscored } from 'underscore.string';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Skeleton,
} from '@cargomatic/ui';
import { Plus, Search as SearchIcon, X } from 'lucide-react';
import Container from '../../v2/container';
import AccessorialCard from './AccessorialCard';
import AccessorialForm from './AccessorialForm';
import SessionStore from '../../../stores/SessionStore';
import { FilterDropdown } from '../../design-system/molecules/FilterDropdown';
import {
  getAccessorials,
  deleteAccessorial,
} from '../../../utils/AccessorialWebAPIUtils';
import ConfirmModal from '../../ui/modals/ConfirmModal';
import ToastStore from '../../../stores/ToastStore';

const shipmentTypes = [
  {
    localizationSlug: 'DRAYAGE',
    value: 'drayage',
  },
  {
    localizationSlug: 'SHORTHAUL',
    value: 'shorthaul',
  },
];

class AccessorialManagementCenter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      accessorials: [],
      showAccessorialModal: false,
      showConfirmDelete: false,
      selectedAccessorial: null,
      selectedShipmentTypes: [],
      filters: {
        shipment_type: '',
        name: '',
        page: 1,
        per_page: 40,
        paginate: true,
      },
      pages: 1,
      canCreate: false,
      canEdit: false,
      canDelete: false,
    };

    this.fetchAccessorials = _.debounce(this.fetchAccessorials, 500);
  }

  componentDidMount() {
    const { t } = this.props;
    this.fetchAccessorials();
    SessionStore.addChangeListener(this.handleSessionStoreChange);
    this.setAccessLevel();
    document.title = t('CARGOMATIC_INC_ACCESSORIALS');
  }

  componentWillUnmount() {
    SessionStore.removeChangeListener(this.handleSessionStoreChange);
  }

  fetchAccessorials = async () => {
    this.setState({ loading: true }, async () => {
      try {
        const result = await getAccessorials(this.getFilters());
        const {
          entity: { items, pages },
        } = result;

        const accessorials = items.map((a) => ({
          ...a,
          slug: a.slug || underscored(a.name.toLowerCase()),
        }));

        this.setState({
          accessorials,
          loading: false,
          pages,
        });
      } catch (e) {
        console.error(e);
        this.setState({ loading: false });
      }
    });
  };

  getFilters() {
    const { filters } = this.state;

    return Object.keys(filters).reduce((runningMap, key) => {
      if (filters[key]) {
        return {
          ...runningMap,
          [key]: filters[key],
        };
      }
      return { ...runningMap };
    }, {});
  }

  getConfirmDeleteTitle() {
    const { t } = this.props;
    if (!this.state.showConfirmDelete) {
      return '';
    }

    return t('DELETE_ACCESSORIAL', {
      deleteAccessorial: this.state.selectedAccessorial.name,
    });
  }

  getModalTitle() {
    const { t } = this.props;
    const { selectedAccessorial } = this.state;

    if (selectedAccessorial) {
      return t('EDIT_ACCESSORIAL');
    }

    return t('ADD_ACCESSORIAL', { count: 1 });
  }

  setAccessLevel() {
    const canAccess =
      SessionStore.isGodAdmin() || SessionStore.hasAccessorialManagerRole();
    this.setState({
      canDelete: canAccess,
      canCreate: canAccess,
      canEdit: canAccess,
    });
  }

  handleSessionStoreChange = () => {
    this.setAccessLevel();
  };

  handleSelectPage = (eventKey) => {
    this.setState(
      (prevState) => {
        return {
          filters: {
            ...prevState.filters,
            page: eventKey,
          },
        };
      },
      () => this.fetchAccessorials(),
    );
  };

  handleToggleShipmentType = (value, checked) => {
    this.setState(
      (prevState) => {
        const current = prevState.selectedShipmentTypes || [];
        const next = checked
          ? Array.from(new Set([...current, value]))
          : current.filter((v) => v !== value);
        return {
          selectedShipmentTypes: next,
          filters: {
            ...prevState.filters,
            shipment_type: next.length === 1 ? next[0] : '',
            page: 1,
          },
        };
      },
      () => this.fetchAccessorials(),
    );
  };

  handleClearShipmentTypes = () => {
    this.setState(
      (prevState) => ({
        selectedShipmentTypes: [],
        filters: { ...prevState.filters, shipment_type: '', page: 1 },
      }),
      () => this.fetchAccessorials(),
    );
  };

  handleSearch = (name) => {
    this.setState(
      (prevState) => {
        return {
          filters: {
            ...prevState.filters,
            name,
            page: 1,
          },
        };
      },
      () => {
        this.fetchAccessorials();
      },
    );
  };

  handleConfirmDelete = async () => {
    const { t } = this.props;
    try {
      await deleteAccessorial(this.state.selectedAccessorial);
      this.setState((prevState) => {
        const { accessorials: currentAccessorials, selectedAccessorial } =
          prevState;
        const index = currentAccessorials.findIndex(
          (a) => a._id === selectedAccessorial._id,
        );
        return {
          accessorials: [
            ...currentAccessorials.slice(0, index),
            ...currentAccessorials.slice(index + 1),
          ],
          selectedAccessorial: null,
          showConfirmDelete: false,
        };
      });
      ToastStore.addNotification({
        level: 'success',
        title: t('ACCESSORIAL_DELETED_SUCCESSFULLY'),
      });
    } catch (e) {
      console.error(e);
      ToastStore.addNotification({
        level: 'error',
        title: t('PROBLEM_DELETE_ACCESSORIAL'),
      });
    }
  };

  handleDeleteAccessorial = (id) => () => {
    this.setState((prevState) => {
      const accessorial = prevState.accessorials.find((a) => a._id === id);
      if (!accessorial) {
        return null;
      }

      return {
        showConfirmDelete: true,
        selectedAccessorial: accessorial,
      };
    });
  };

  handleHideConfirmModal = () => {
    this.setState({ showConfirmDelete: false, selectedAccessorial: null });
  };

  handleAddAccessorial = () => {
    this.setState({ showAccessorialModal: true });
  };

  handleEditAccessorial = (id) => () => {
    this.setState((prevState) => {
      if (!prevState.canEdit) {
        return null;
      }
      const accessorial = prevState.accessorials.find((a) => a._id === id);

      return {
        showAccessorialModal: true,
        selectedAccessorial: accessorial,
      };
    });
  };

  handleSaveAccessorialSuccess = () => {
    this.handleHideAccessorialModal();
    this.fetchAccessorials();
  };

  handleHideAccessorialModal = () => {
    this.setState({ showAccessorialModal: false, selectedAccessorial: null });
  };

  renderSkeletonGrid() {
    const placeholders = Array.from({ length: this.state.filters.per_page });
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
        {placeholders.map((_unused, i) => (
          <Skeleton key={i} className="h-40 w-full rounded" />
        ))}
      </div>
    );
  }

  renderAccessorials() {
    const { t } = this.props;
    if (this.state.loading) {
      return this.renderSkeletonGrid();
    }
    if (this.state.accessorials.length === 0) {
      return <p>{t('NO_ACCESSORIALS')}</p>;
    }

    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
        {this.state.accessorials.map((accessorial) => (
          <AccessorialCard
            key={accessorial._id}
            name={accessorial.name}
            iconSlug={accessorial.icon_slug}
            slug={accessorial.slug}
            size="auto"
            canSelect={this.state.canEdit}
            canDelete={this.state.canDelete}
            onSelect={this.handleEditAccessorial(accessorial._id)}
            onDelete={this.handleDeleteAccessorial(accessorial._id)}
          />
        ))}
      </div>
    );
  }

  render() {
    const { t } = this.props;

    return (
      <Container>
        <div className="min-h-screen mt-10">
          <div className="flex items-center mb-4">
            <h3 className="my-0 mr-2 text-lg font-medium">
              {t('ACCESSORIAL_MANAGEMENT_CENTER')}
            </h3>
            {this.state.canCreate && (
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={this.handleAddAccessorial}
                aria-label={t('ADD_ACCESSORIAL', { count: 1 })}
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-6">
            <div className="relative w-full sm:w-72">
              <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                value={this.state.filters.name}
                placeholder={t('SEARCH')}
                onChange={(e) => this.handleSearch(e.target.value)}
                className="h-8 pl-8 pr-8"
                aria-label={t('SEARCH')}
              />
              {this.state.filters.name && (
                <button
                  type="button"
                  onClick={() => this.handleSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={t('CLEAR')}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="w-44">
              <FilterDropdown
                title={t('SHIPMENT_TYPE')}
                selected={this.state.selectedShipmentTypes}
                options={shipmentTypes.map((type) => ({
                  value: type.value,
                  label: t(type.localizationSlug),
                }))}
                onCheckedChange={this.handleToggleShipmentType}
                onClear={this.handleClearShipmentTypes}
              />
            </div>
          </div>
          <div className="mb-4">
            {this.renderAccessorials()}
            {this.state.pages > 1 && (
              <Pagination
                first
                last
                ellipsis
                items={this.state.pages}
                maxButtons={10}
                activePage={this.state.filters.page}
                onSelect={this.handleSelectPage}
              />
            )}
          </div>
          {this.state.showAccessorialModal && (
            <Dialog
              open={this.state.showAccessorialModal}
              onOpenChange={(open) => {
                if (!open) this.handleHideAccessorialModal();
              }}
            >
              <DialogContent className="sm:max-w-[720px] max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
                <DialogHeader className="px-6 pt-5 pb-4 border-b border-border shrink-0">
                  <DialogTitle>{this.getModalTitle()}</DialogTitle>
                </DialogHeader>
                <AccessorialForm
                  accessorial={this.state.selectedAccessorial}
                  onCancel={this.handleHideAccessorialModal}
                  onSaveSuccess={this.handleSaveAccessorialSuccess}
                />
              </DialogContent>
            </Dialog>
          )}
          {this.state.showConfirmDelete && (
            <ConfirmModal
              show={this.state.showConfirmDelete}
              title={this.getConfirmDeleteTitle()}
              onConfirm={this.handleConfirmDelete}
              onHide={this.handleHideConfirmModal}
              message={t('ARE_YOU_SURE')}
            />
          )}
        </div>
      </Container>
    );
  }
}

export default withTranslation()(AccessorialManagementCenter);
